import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { formatSydneyTime } from '@/utils/timezone';
import { Button } from '@/components/Button';
import { Badge } from '@/components/Badge';
import { Avatar } from '@/components/Avatar';
import {
  TicketStatus,
  TicketPriority,
  type Ticket,
  type User,
  type UpdateTicketRequest,
  getTicketStatusString,
  getTicketPriorityString
} from '@/types/api';
import { userService } from '@/services/userService';
import { ticketService } from '@/services/ticketService';
import { attachmentService, type AttachmentDto } from '@/services/attachmentService';
import { useAuth } from '@/contexts/AuthContext';
import { getRoleDisplayName } from '@/utils/roleUtils';

interface PropertiesPanelProps {
  ticket: Ticket;
  onTicketUpdate: (updates: Partial<Ticket>) => void;
  canEdit: boolean;
  className?: string;
}

/**
 * Properties Panel Component
 * Displays ticket properties, customer info, and assignment controls
 */
export const PropertiesPanel: React.FC<PropertiesPanelProps> = ({
  ticket,
  onTicketUpdate,
  canEdit,
  className,
  ...props
}) => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [isUpdating, setIsUpdating] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);

  const [showAssignmentDropdown, setShowAssignmentDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [showTagInput, setShowTagInput] = useState(false);
  const [availableAgents, setAvailableAgents] = useState<User[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [attachments, setAttachments] = useState<AttachmentDto[]>([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);

  // Refs for click-outside functionality
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const priorityDropdownRef = useRef<HTMLDivElement>(null);
  const assignmentDropdownRef = useRef<HTMLDivElement>(null);

  // Load ticket attachments
  useEffect(() => {
    const loadAttachments = async () => {
      if (!ticket?.id) return;
      try {
        setLoadingAttachments(true);
        const data = await attachmentService.getTicketAttachments(ticket.id);
        setAttachments(data || []);
      } catch (err) {
        console.error('Failed to load ticket attachments:', err);
      } finally {
        setLoadingAttachments(false);
      }
    };
    loadAttachments();
  }, [ticket?.id]);
  
  // Load available agents from API
  useEffect(() => {
    const loadAgents = async () => {
      if (!canEdit) {
        return; // Only load agents if user can edit
      }

      try {
        setLoadingAgents(true);
        const response = await userService.getAllAgents();

        if (response.success && response.data) {
          setAvailableAgents(response.data.data);
        } else {
          console.error('Failed to load agents:', response.message);
        }
      } catch (error) {
        console.error('Error loading agents:', error);
      } finally {
        setLoadingAgents(false);
      }
    };

    loadAgents();
  }, [canEdit]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
        setShowStatusDropdown(false);
      }
      if (priorityDropdownRef.current && !priorityDropdownRef.current.contains(event.target as Node)) {
        setShowPriorityDropdown(false);
      }
      if (assignmentDropdownRef.current && !assignmentDropdownRef.current.contains(event.target as Node)) {
        setShowAssignmentDropdown(false);
      }
    };

    if (showStatusDropdown || showPriorityDropdown || showAssignmentDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showStatusDropdown, showPriorityDropdown, showAssignmentDropdown]);

  const statusOptions = [
    { value: TicketStatus.Open, label: 'Open', color: 'blue' },
    { value: TicketStatus.Pending, label: 'Pending', color: 'yellow' },
    { value: TicketStatus.OnHold, label: 'On Hold', color: 'gray' },
    { value: TicketStatus.Resolved, label: 'Resolved', color: 'green' },
    { value: TicketStatus.Closed, label: 'Closed', color: 'gray' }
  ];
  
  const priorityOptions = [
    { value: TicketPriority.Low, label: 'Low', color: 'gray' },
    { value: TicketPriority.Medium, label: 'Medium', color: 'yellow' },
    { value: TicketPriority.High, label: 'High', color: 'orange' },
    { value: TicketPriority.Urgent, label: 'Urgent', color: 'red' }
  ];
  
  // Update handlers
  const updateTicketField = async (field: 'tags', value: string[]) => {
    if (!canEdit) return;

    try {
      setIsUpdating(field);
      setUpdateError(null);

      const updateData: UpdateTicketRequest = { tags: value.join(', ') };

      // Call real API to update ticket
      const response = await ticketService.updateTicket(ticket.id, updateData);

      if (response.success && response.data) {
        // Update the parent component with the new ticket data
        onTicketUpdate(response.data);
      } else {
        throw new Error(response.message || 'Failed to update ticket');
      }

      // Close dropdowns (but not assignment dropdown since this shouldn't handle assignments)
      setShowStatusDropdown(false);
      setShowPriorityDropdown(false);

    } catch (error) {
      console.error(`Failed to update ${field}:`, error);
      setUpdateError(`Could not save the ${field} change. Please try again.`);
    } finally {
      setIsUpdating(null);
    }
  };
  
  const handleStatusChange = async (status: TicketStatus) => {
    if (!canEdit) return;

    try {
      setIsUpdating('status');
      setUpdateError(null);

      const response = await ticketService.updateTicketStatus(ticket.id, status);

      if (response.success && response.data) {
        onTicketUpdate(response.data);
        setShowAssignmentDropdown(false);
        setShowStatusDropdown(false);
        setShowPriorityDropdown(false);
      } else {
        throw new Error(response.message || 'Failed to update ticket status');
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      setUpdateError('Could not save the status change. Please try again.');
    } finally {
      setIsUpdating(null);
    }
  };
  
  const handlePriorityChange = async (priority: TicketPriority) => {
    if (!canEdit) return;

    try {
      setIsUpdating('priority');
      setUpdateError(null);

      const response = await ticketService.updateTicketPriority(ticket.id, priority);

      if (response.success && response.data) {
        onTicketUpdate(response.data);
        setShowAssignmentDropdown(false);
        setShowStatusDropdown(false);
        setShowPriorityDropdown(false);
      } else {
        throw new Error(response.message || 'Failed to update ticket priority');
      }
    } catch (error) {
      console.error('Failed to update priority:', error);
      setUpdateError('Could not save the priority change. Please try again.');
    } finally {
      setIsUpdating(null);
    }
  };

  const handleAssignmentChange = async (agentId: string | null) => {
    if (!canEdit) {
      return;
    }

    try {
      setIsUpdating('assignedTo');
      setUpdateError(null);

      const response = await ticketService.assignTicket(ticket.id, agentId);

      if (response.success && response.data) {
        onTicketUpdate(response.data);
        setShowAssignmentDropdown(false);
        setShowStatusDropdown(false);
        setShowPriorityDropdown(false);
      } else {
        throw new Error(response.message || 'Failed to update assignment');
      }
    } catch (error) {
      console.error('Failed to update assignment:', error);
      setUpdateError('Could not save the assignment change. Please try again.');
    } finally {
      setIsUpdating(null);
    }
  };

  const handleTagAdd = (tag: string) => {
    const currentTags = ticket.tags && Array.isArray(ticket.tags) ? ticket.tags : [];
    if (!tag.trim() || currentTags.includes(tag.trim())) return;
    
    const newTags = [...currentTags, tag.trim()];
    updateTicketField('tags', newTags);
    setNewTag('');
    setShowTagInput(false);
  };
  
  const handleTagRemove = (tagToRemove: string) => {
    const currentTags = ticket.tags && Array.isArray(ticket.tags) ? ticket.tags : [];
    const newTags = currentTags.filter(tag => tag !== tagToRemove);
    updateTicketField('tags', newTags);
  };
  
  // Format date for display
  const formatDate = (dateString: string) => formatSydneyTime(dateString);
  
  // Get status badge
  const getStatusBadge = (status: TicketStatus | number) => {
    // Convert numeric status to string enum value if needed
    let normalizedStatus: TicketStatus;
    if (typeof status === 'number') {
      switch (status) {
        case 0: normalizedStatus = TicketStatus.New; break;
        case 1: normalizedStatus = TicketStatus.Open; break;
        case 2: normalizedStatus = TicketStatus.Pending; break;
        case 3: normalizedStatus = TicketStatus.Resolved; break;
        case 4: normalizedStatus = TicketStatus.Closed; break;
        case 5: normalizedStatus = TicketStatus.OnHold; break;
        case 7: normalizedStatus = TicketStatus.Cancelled; break;
        default: normalizedStatus = TicketStatus.Open; break;
      }
    } else {
      normalizedStatus = status;
    }

    const statusConfig = {
      [TicketStatus.New]: { variant: 'info' as const, label: 'New' },
      [TicketStatus.Open]: { variant: 'info' as const, label: 'Open' },
      [TicketStatus.InProgress]: { variant: 'warning' as const, label: 'In Progress' },
      [TicketStatus.Pending]: { variant: 'warning' as const, label: 'Pending' },
      [TicketStatus.OnHold]: { variant: 'secondary' as const, label: 'On Hold' },
      [TicketStatus.Resolved]: { variant: 'success' as const, label: 'Resolved' },
      [TicketStatus.Closed]: { variant: 'secondary' as const, label: 'Closed' },
      [TicketStatus.Cancelled]: { variant: 'secondary' as const, label: 'Cancelled' }
    };

    const config = statusConfig[normalizedStatus] || { variant: 'secondary' as const, label: getTicketStatusString(status) };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  // Get priority badge
  const getPriorityBadge = (priority: TicketPriority | number) => {
    // Convert numeric priority to string enum value if needed
    let normalizedPriority: TicketPriority;
    if (typeof priority === 'number') {
      switch (priority) {
        case 0: normalizedPriority = TicketPriority.Low; break;
        case 1: normalizedPriority = TicketPriority.Medium; break;
        case 2: normalizedPriority = TicketPriority.High; break;
        case 3: normalizedPriority = TicketPriority.Urgent; break;
        case 4: normalizedPriority = TicketPriority.Critical; break;
        default: normalizedPriority = TicketPriority.Medium; break;
      }
    } else {
      normalizedPriority = priority;
    }

    const priorityConfig = {
      [TicketPriority.Low]: { variant: 'secondary' as const, label: 'Low' },
      [TicketPriority.Medium]: { variant: 'warning' as const, label: 'Medium' },
      [TicketPriority.High]: { variant: 'error' as const, label: 'High' },
      [TicketPriority.Urgent]: { variant: 'error' as const, label: 'Urgent' },
      [TicketPriority.Critical]: { variant: 'error' as const, label: 'Critical' }
    };

    const config = priorityConfig[normalizedPriority] || { variant: 'secondary' as const, label: getTicketPriorityString(priority) };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  
  return (
    <div className={clsx('flex flex-col h-full bg-white', className)} {...props}>
      {/* Panel Header */}
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
        <h2 className="text-sm font-semibold text-gray-900">Ticket Properties</h2>
      </div>

      {updateError && (
        <div
          role="alert"
          data-testid="properties-update-error"
          className="mx-4 mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
        >
          {updateError}
        </div>
      )}
      
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Status Management */}
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 block">
              Status
            </label>
            <div className="relative">
              <button
                onClick={() => {
                  if (canEdit) {
                    setShowStatusDropdown(!showStatusDropdown);
                    setShowPriorityDropdown(false);
                    setShowAssignmentDropdown(false);
                  }
                }}
                disabled={!canEdit || isUpdating === 'status'}
                className={clsx(
                  'w-full flex items-center justify-between p-2 border rounded-md',
                  canEdit ? 'hover:bg-gray-50' : 'cursor-not-allowed',
                  'focus:outline-none focus:ring-2 focus:ring-primary-500'
                )}
              >
                {getStatusBadge(ticket.status)}
                {canEdit && (
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                )}
              </button>
              
              {showStatusDropdown && canEdit && (
                <div ref={statusDropdownRef} className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-[100]">
                  {statusOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleStatusChange(option.value)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 first:rounded-t-md last:rounded-b-md"
                    >
                      <Badge variant={option.value === ticket.status ? 'primary' : 'secondary'}>
                        {option.label}
                      </Badge>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 block">
              Priority
            </label>
            <div className="relative">
              <button
                onClick={() => {
                  if (canEdit) {
                    setShowPriorityDropdown(!showPriorityDropdown);
                    setShowStatusDropdown(false);
                    setShowAssignmentDropdown(false);
                  }
                }}
                disabled={!canEdit || isUpdating === 'priority'}
                className={clsx(
                  'w-full flex items-center justify-between p-2 border rounded-md',
                  canEdit ? 'hover:bg-gray-50' : 'cursor-not-allowed',
                  'focus:outline-none focus:ring-2 focus:ring-primary-500'
                )}
              >
                {getPriorityBadge(ticket.priority)}
                {canEdit && (
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                )}
              </button>
              
              {showPriorityDropdown && canEdit && (
                <div ref={priorityDropdownRef} className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-[100]">
                  {priorityOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handlePriorityChange(option.value)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 first:rounded-t-md last:rounded-b-md"
                    >
                      <Badge variant={option.value === ticket.priority ? 'primary' : 'secondary'}>
                        {option.label}
                      </Badge>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Assignment */}
        {canEdit && (
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 block">
              Assigned Agent
            </label>
            <div className="relative">
              <button
                onClick={() => {
                  setShowAssignmentDropdown(!showAssignmentDropdown);
                  setShowStatusDropdown(false);
                  setShowPriorityDropdown(false);
                }}
                disabled={isUpdating === 'assignedTo'}
                className="w-full flex items-center justify-between p-2 border rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <div className="flex items-center space-x-2">
                  {ticket.assignedTo ? (
                    <>
                      <Avatar
                        name={ticket.assignedTo.fullName}
                        src={ticket.assignedTo.profileImageUrl}
                        size="sm"
                      />
                      <div className="text-left">
                        <div className="text-sm font-medium text-gray-900">
                          {ticket.assignedTo.fullName}
                        </div>
                        <div className="text-xs text-gray-500">
                          {ticket.assignedTo.email}
                        </div>
                      </div>
                    </>
                  ) : (
                    <span className="text-sm text-gray-500">Unassigned</span>
                  )}
                </div>
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {showAssignmentDropdown && (
                <div ref={assignmentDropdownRef} className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-[100]">
                  <button
                    onClick={() => handleAssignmentChange(null)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 first:rounded-t-md"
                  >
                    <span className="text-sm text-gray-500">Unassign</span>
                  </button>
                  {loadingAgents ? (
                    <div className="px-3 py-2 text-sm text-gray-500">
                      Loading agents...
                    </div>
                  ) : availableAgents.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-gray-500">
                      No agents available
                    </div>
                  ) : (
                    availableAgents.map((agent) => (
                    <button
                      key={agent.id}
                      onClick={() => handleAssignmentChange(agent.id)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 last:rounded-b-md"
                    >
                      <div className="flex items-center space-x-2">
                        <Avatar
                          name={agent.fullName}
                          src={agent.profileImageUrl}
                          size="sm"
                        />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {agent.fullName}
                          </div>
                          <div className="text-xs text-gray-500">
                            {agent.email}
                          </div>
                        </div>
                      </div>
                    </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Tags */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Tags
            </label>
            {canEdit && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowTagInput(true)}
                className="px-1 py-1"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </Button>
            )}
          </div>
          
          <div className="space-y-0">
            {/* Tags Display - only show container when tags exist */}
            {(ticket.tags && Array.isArray(ticket.tags) && ticket.tags.length > 0) && (
              <div className="flex flex-wrap gap-2 mb-3">
                {ticket.tags.map((tag) => (
                  <div
                    key={tag}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-full min-h-[32px] border border-gray-200 hover:bg-gray-200 transition-colors"
                  >
                    <span className="truncate max-w-32">{tag}</span>
                    {canEdit && (
                      <button
                        onClick={() => handleTagRemove(tag)}
                        className="p-0.5 rounded-full hover:bg-gray-300 text-gray-500 hover:text-gray-700 transition-colors focus:outline-none"
                        title={`Remove ${tag} tag`}
                        aria-label={`Remove ${tag} tag`}
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {showTagInput && (
              <div className={`flex gap-2 items-start ${(ticket.tags && ticket.tags.length > 0) ? '' : 'mt-0'}`}>
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="Add a tag..."
                  autoFocus
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm min-h-[32px] focus:border-primary-500 focus:ring-1 focus:ring-primary-500 placeholder-gray-400 transition-all duration-200"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newTag.trim()) {
                      e.preventDefault();
                      handleTagAdd(newTag);
                    } else if (e.key === 'Escape') {
                      setShowTagInput(false);
                      setNewTag('');
                    }
                  }}
                />
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleTagAdd(newTag)}
                  disabled={!newTag.trim()}
                  className="min-h-[32px]"
                >
                  Add
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowTagInput(false);
                    setNewTag('');
                  }}
                  className="min-h-[32px]"
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>
        </div>
        
        {/* Ticket Metadata */}
        <div className="space-y-3">
          <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">Details</h3>
          
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Source</span>
              <span className="text-gray-900">{ticket.source}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-500">Created</span>
              <time
                dateTime={ticket.createdAt}
                className="text-gray-900"
                title={formatDate(ticket.createdAt)}
              >
                {formatDate(ticket.createdAt)}
              </time>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-500">Updated</span>
              <time
                dateTime={ticket.updatedAt}
                className="text-gray-900"
                title={formatDate(ticket.updatedAt)}
              >
                {formatDate(ticket.updatedAt)}
              </time>
            </div>
            
            {ticket.resolvedAt && (
              <div className="flex justify-between">
                <span className="text-gray-500">Resolved</span>
                <time
                  dateTime={ticket.resolvedAt}
                  className="text-gray-900"
                  title={formatDate(ticket.resolvedAt)}
                >
                  {formatDate(ticket.resolvedAt)}
                </time>
              </div>
            )}
          </div>
        </div>

        {/* Attachments Section */}
        <div className="border-t border-gray-200 pt-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Attachments ({attachments.length})
            </h3>
          </div>

          {loadingAttachments ? (
            <div className="text-xs text-gray-500 py-2">Loading attachments...</div>
          ) : attachments.length === 0 ? (
            <div className="text-xs text-gray-400 py-1 italic">No attachments for this ticket.</div>
          ) : (
            <div className="space-y-2">
              {attachments.map((att) => (
                <div
                  key={att.id}
                  className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 text-xs"
                >
                  <div className="flex items-center space-x-2 truncate flex-1 mr-2">
                    <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                    <span className="font-medium text-gray-800 dark:text-gray-200 truncate" title={att.displayName || att.fileName}>
                      {att.displayName || att.fileName}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="p-1 h-auto text-primary-600 hover:text-primary-800"
                    onClick={async () => {
                      try {
                        const blob = await attachmentService.downloadAttachment(att.id);
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = att.fileName || 'download';
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        document.body.removeChild(a);
                      } catch (err) {
                        console.error('Failed to download attachment:', err);
                      }
                    }}
                    title={`Download ${att.displayName || att.fileName}`}
                    aria-label={`Download ${att.displayName || att.fileName}`}
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Customer Information */}
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">Customer</h3>
          
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <Avatar
                name={ticket.requester.fullName}
                src={ticket.requester.profileImageUrl}
                size="lg"
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-900">
                  {ticket.requester.fullName}
                </div>
                <div className="text-sm text-gray-500">
                  {ticket.requester.email}
                </div>
                <Badge variant="secondary" size="sm">
                  {getRoleDisplayName(ticket.requester.role)}
                </Badge>
              </div>
            </div>
            
            {ticket.company && (
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Company</span>
                  <span className="text-sm font-medium text-gray-900">
                    {ticket.company.name}
                  </span>
                </div>
                {ticket.company.domain && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Domain</span>
                    <span className="text-sm text-gray-900">
                      {ticket.company.domain}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Customer Quick Actions */}
          <div className="mt-4 space-y-2">
            <Button
              variant="outline"
              size="sm"
              fullWidth
              onClick={() => window.open(`mailto:${ticket.requester.email}`)}
              className="flex items-center justify-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M3 8l7.89 4.95a1 1 0 001.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span>Email Customer</span>
            </Button>
            
            {isAdmin() && (
              <Button
                variant="outline"
                size="sm"
                fullWidth
                onClick={() => navigate(`/admin/users/${ticket.requester.id}/edit`)}
                className="flex items-center justify-center space-x-2"
                title="Open this requester's profile in user management"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span>Manage User</span>
              </Button>
            )}
            
            <Button
              variant="outline"
              size="sm"
              fullWidth
              onClick={() => navigate(`/tickets?requester=${ticket.requester.id}`)}
              className="flex items-center justify-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <span>Other Tickets</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
